// useLayoutEditorBlockMutations.js

import { useCallback } from 'react';

// Utilities
import { generateKey } from 'utilities/Keys';
import {
	normalizeRichTextValue,
	trimTrailingEmptyRichTextBlocks,
} from 'utilities/RichText';
import { normalizePageNodePresentation } from './LayoutNodePresentation';
import { normalizeFieldBlockFieldKeys } from './layoutFieldDisplay';
import {
	cloneBlocksByKey,
	cloneLayout,
	getNodeBlockKey,
} from './pageEditorLayoutUtils';

function normalizeString(value) {
	return String(value || '').trim();
}

/**
 * useLayoutEditorBlockMutations
 *
 * Owns the block assignment and persistence handlers for view, fields, node, rich-text, and rollup blocks.
 *
 * @param {object} params - Hook parameters.
 * @param {number} params.assignedBlockCount - Count of currently assigned blocks.
 * @param {object} params.blocksByKey - Current block lookup map.
 * @param {string} params.fieldBlockSlotKey - Active field-block slot key.
 * @param {function|null} params.handleCloseFieldBlockModal - Closes the field block modal.
 * @param {function|null} params.handleCloseNodeBlockModal - Closes the node block modal.
 * @param {function|null} params.handleCloseRichTextBlockModal - Closes the rich-text block modal.
 * @param {function|null} params.handleCloseRollupBlockModal - Closes the rollup block modal.
 * @param {function|null} params.handleCloseViewBlockModal - Closes the view block modal.
 * @param {object} params.layout - Current layout tree.
 * @param {string} params.nodeBlockSlotKey - Active node-block slot key.
 * @param {function|null} params.onPersistNodeBlock - Optional node-block persistence override.
 * @param {function|null} params.onPersistRichTextBlock - Optional rich-text block persistence override.
 * @param {function|null} params.onPersistRollupBlock - Optional rollup block persistence override.
 * @param {function|null} params.onPersistViewBlock - Optional view block persistence override.
 * @param {function} params.persistPage - Persists layout and block changes together.
 * @param {string} params.resolvedViewBlockTypeKey - Resolved default type key for view blocks.
 * @param {string} params.richTextBlockSlotKey - Active rich-text block slot key.
 * @param {string} params.rollupBlockSlotKey - Active rollup block slot key.
 * @param {function} params.setInlineRichTextDraftValue - Updates the inline rich-text draft value.
 * @param {function} params.setInlineRichTextSlotKey - Updates the active inline rich-text slot key.
 * @param {boolean} params.useLocalViewDefinitions - Whether page-local view payloads should be stored inline.
 * @param {string} params.viewBlockSlotKey - Active view-block slot key.
 *
 * @returns {object} Block mutation handlers used by LayoutEditor.
 */
const useLayoutEditorBlockMutations = ({
	assignedBlockCount,
	blocksByKey,
	fieldBlockSlotKey,
	fieldBlockType = 'FIELDS',
	handleCloseFieldBlockModal,
	handleCloseNodeBlockModal,
	handleCloseRichTextBlockModal,
	handleCloseRollupBlockModal,
	handleCloseViewBlockModal,
	layout,
	nodeBlockSlotKey,
	onPersistNodeBlock,
	onPersistRichTextBlock,
	onPersistRollupBlock,
	onPersistViewBlock,
	persistPage,
	resolvedViewBlockTypeKey,
	richTextBlockSlotKey,
	rollupBlockSlotKey,
	setInlineRichTextDraftValue,
	setInlineRichTextSlotKey,
	useLocalViewDefinitions,
	viewBlockSlotKey,
}) => {
	const handleAssignViewBlock = useCallback(async ({ relatedTypeKey, titleOverride, typeKey, view, viewId }) => {
		const slotKey = normalizeString(viewBlockSlotKey);
		const cleanedViewId = normalizeString(viewId);
		const nextView = view && typeof view === 'object'
			? view
			: null;
		const cleanedTypeKey = normalizeString(typeKey || resolvedViewBlockTypeKey || nextView?.nodeType || nextView?.typeKey);
		const cleanedRelatedTypeKey = normalizeString(relatedTypeKey || nextView?.nodeType || nextView?.nodeTypeKey || nextView?.typeKey || cleanedTypeKey);
		if (!slotKey) return;
		if (useLocalViewDefinitions) {
			if (!nextView || !cleanedTypeKey) return;
		} else if (!cleanedViewId || !cleanedRelatedTypeKey) {
			return;
		}

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[slotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();

		const resolvedTitleOverride = normalizeString(titleOverride)
			|| normalizeString(nextView?.title)
			|| null;

		nextBlocksByKey[blockKey] = {
			key: blockKey,
			titleOverride: resolvedTitleOverride,
			type: 'VIEW',
			...(useLocalViewDefinitions ? {
				typeKey: cleanedTypeKey,
				view: {
					...nextView,
					nodeType: cleanedTypeKey,
				},
			} : {
				relatedTypeKey: cleanedRelatedTypeKey,
				viewId: cleanedViewId,
			}),
		};
		slotNode.blockKey = blockKey;

		if (typeof onPersistViewBlock === 'function') {
			await onPersistViewBlock({
				blockKey,
				nextBlock: nextBlocksByKey[blockKey],
				nextBlocksByKey,
				nextLayout,
				slotKey,
			});
			handleCloseViewBlockModal?.();
			return;
		}

		await persistPage(nextLayout, nextBlocksByKey);
		handleCloseViewBlockModal?.();
	}, [
		blocksByKey,
		handleCloseViewBlockModal,
		layout,
		onPersistViewBlock,
		persistPage,
		resolvedViewBlockTypeKey,
		useLocalViewDefinitions,
		viewBlockSlotKey,
	]);

	const handleAssignFieldBlock = useCallback(async ({ fieldKeys }) => {
		const slotKey = normalizeString(fieldBlockSlotKey);
		const cleanedFieldKeys = normalizeFieldBlockFieldKeys(fieldKeys);
		if (!slotKey || !cleanedFieldKeys.length) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[slotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();

		nextBlocksByKey[blockKey] = {
			fieldKeys: cleanedFieldKeys,
			key: blockKey,
			titleOverride: null,
			type: normalizeString(fieldBlockType).toUpperCase() || 'FIELDS',
		};
		slotNode.blockKey = blockKey;

		await persistPage(nextLayout, nextBlocksByKey);
		handleCloseFieldBlockModal?.();
	}, [blocksByKey, fieldBlockSlotKey, fieldBlockType, handleCloseFieldBlockModal, layout, persistPage]);

	const handleAssignNodeBlock = useCallback(async ({ node, nodeKey, presentation }) => {
		const slotKey = normalizeString(nodeBlockSlotKey);
		const cleanedNodeKey = normalizeString(nodeKey || node?.key);
		if (!slotKey || !cleanedNodeKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[slotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();

		nextBlocksByKey[blockKey] = {
			key: blockKey,
			nodeKey: cleanedNodeKey,
			presentation: normalizePageNodePresentation(presentation),
			titleOverride: null,
			type: 'NODE',
		};
		slotNode.blockKey = blockKey;

		if (typeof onPersistNodeBlock === 'function') {
			await onPersistNodeBlock({
				blockKey,
				nextBlock: nextBlocksByKey[blockKey],
				nextBlocksByKey,
				nextLayout,
				slotKey,
			});
			handleCloseNodeBlockModal?.();
			return;
		}

		await persistPage(nextLayout, nextBlocksByKey);
		handleCloseNodeBlockModal?.();
	}, [
		blocksByKey,
		handleCloseNodeBlockModal,
		layout,
		nodeBlockSlotKey,
		onPersistNodeBlock,
		persistPage,
	]);

	const saveRichTextBlockValue = useCallback(async (slotKey, value) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[cleanedSlotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();
		const normalizedValue = normalizeRichTextValue(value);
		const trimmedValue = {
			...normalizedValue,
			content: trimTrailingEmptyRichTextBlocks(normalizedValue.content),
		};

		nextBlocksByKey[blockKey] = {
			key: blockKey,
			type: 'RICH_TEXT',
			value: trimmedValue,
		};
		slotNode.blockKey = blockKey;

		if (typeof onPersistRichTextBlock === 'function') {
			await onPersistRichTextBlock({
				blockKey,
				nextBlock: nextBlocksByKey[blockKey],
				nextBlocksByKey,
				nextLayout,
				slotKey: cleanedSlotKey,
			});
			return;
		}

		await persistPage(nextLayout, nextBlocksByKey);
	}, [blocksByKey, layout, onPersistRichTextBlock, persistPage]);

	const handleStartInlineRichTextEditing = useCallback((slotKey, value) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		setInlineRichTextSlotKey(cleanedSlotKey);
		setInlineRichTextDraftValue(normalizeRichTextValue(value));
	}, [setInlineRichTextDraftValue, setInlineRichTextSlotKey]);

	const handleCreateInlineRichTextBlock = useCallback(async (slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		await saveRichTextBlockValue(cleanedSlotKey, null);
		handleStartInlineRichTextEditing(cleanedSlotKey, null);
	}, [handleStartInlineRichTextEditing, saveRichTextBlockValue]);

	const handleSaveRichTextBlock = useCallback(async (value) => {
		const slotKey = normalizeString(richTextBlockSlotKey);
		if (!slotKey) return;

		await saveRichTextBlockValue(slotKey, value);
		handleCloseRichTextBlockModal?.();
	}, [handleCloseRichTextBlockModal, richTextBlockSlotKey, saveRichTextBlockValue]);

	const handleInlineRichTextBlur = useCallback(async (slotKey, value) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		await saveRichTextBlockValue(cleanedSlotKey, value);
		setInlineRichTextSlotKey((current) => (current === cleanedSlotKey ? '' : current));
	}, [saveRichTextBlockValue, setInlineRichTextSlotKey]);

	const handleAssignContentZoneBlock = useCallback(async (slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[cleanedSlotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();

		nextBlocksByKey[blockKey] = {
			key: blockKey,
			titleOverride: null,
			type: 'CONTENT_ZONE',
		};
		slotNode.blockKey = blockKey;

		await persistPage(nextLayout, nextBlocksByKey);
	}, [blocksByKey, layout, persistPage]);

	const handleAssignDiscussionBlock = useCallback(async (slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[cleanedSlotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();

		nextBlocksByKey[blockKey] = {
			key: blockKey,
			titleOverride: 'Discussion',
			type: 'DISCUSSION',
		};
		slotNode.blockKey = blockKey;

		await persistPage(nextLayout, nextBlocksByKey);
	}, [blocksByKey, layout, persistPage]);

	const handleAssignCoverBlock = useCallback(async (slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[cleanedSlotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();

		nextBlocksByKey[blockKey] = {
			key: blockKey,
			type: 'COVER',
		};
		slotNode.blockKey = blockKey;

		await persistPage(nextLayout, nextBlocksByKey);
	}, [blocksByKey, layout, persistPage]);

	const handleAssignRollupBlock = useCallback(async ({ rollupKey, typeKey }) => {
		const slotKey = normalizeString(rollupBlockSlotKey);
		const cleanedRollupKey = normalizeString(rollupKey);
		const cleanedTypeKey = normalizeString(typeKey);
		if (!slotKey || !cleanedRollupKey || !cleanedTypeKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[slotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();

		nextBlocksByKey[blockKey] = {
			key: blockKey,
			rollupKey: cleanedRollupKey,
			titleOverride: null,
			type: 'ROLLUP',
			typeKey: cleanedTypeKey,
		};
		slotNode.blockKey = blockKey;

		if (typeof onPersistRollupBlock === 'function') {
			await onPersistRollupBlock({
				blockKey,
				nextBlock: nextBlocksByKey[blockKey],
				nextBlocksByKey,
				nextLayout,
				slotKey,
			});
			handleCloseRollupBlockModal?.();
			return;
		}

		await persistPage(nextLayout, nextBlocksByKey);
		handleCloseRollupBlockModal?.();
	}, [
		blocksByKey,
		handleCloseRollupBlockModal,
		layout,
		onPersistRollupBlock,
		persistPage,
		rollupBlockSlotKey,
	]);

	const handleSetNodeBlockPresentation = useCallback(async (slotKey, presentation) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[cleanedSlotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const blockKey = getNodeBlockKey(slotNode);
		if (!blockKey) return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlock = nextBlocksByKey[blockKey];
		if (!existingBlock) return;

		nextBlocksByKey[blockKey] = {
			...existingBlock,
			presentation: normalizePageNodePresentation(presentation),
		};

		if (typeof onPersistNodeBlock === 'function') {
			await onPersistNodeBlock({
				blockKey,
				nextBlock: nextBlocksByKey[blockKey],
				nextBlocksByKey,
				nextLayout,
				slotKey: cleanedSlotKey,
			});
			return;
		}

		await persistPage(nextLayout, nextBlocksByKey);
	}, [blocksByKey, layout, onPersistNodeBlock, persistPage]);

	const handleAssignVideoBlock = useCallback(async (slotKey, nodeKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		const cleanedNodeKey = normalizeString(nodeKey);
		if (!cleanedSlotKey || !cleanedNodeKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[cleanedSlotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		const existingBlockKey = getNodeBlockKey(slotNode);
		const blockKey = existingBlockKey || generateKey();

		nextBlocksByKey[blockKey] = {
			key: blockKey,
			nodeKey: cleanedNodeKey,
			type: 'VIDEO',
		};
		slotNode.blockKey = blockKey;

		await persistPage(nextLayout, nextBlocksByKey);
	}, [blocksByKey, layout, persistPage]);

	const handleRemoveBlockFromSlot = useCallback(async (slotKey) => {
		const cleanedSlotKey = normalizeString(slotKey);
		if (!cleanedSlotKey) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[cleanedSlotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const blockKey = getNodeBlockKey(slotNode);
		if (!blockKey) return;
		if (assignedBlockCount <= 1) return;

		const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
		delete nextBlocksByKey[blockKey];
		slotNode.blockKey = null;

		await persistPage(nextLayout, nextBlocksByKey);
	}, [assignedBlockCount, blocksByKey, layout, persistPage]);

	return {
		handleAssignContentZoneBlock,
		handleAssignCoverBlock,
		handleAssignDiscussionBlock,
		handleAssignFieldBlock,
		handleAssignNodeBlock,
		handleAssignRollupBlock,
		handleAssignVideoBlock,
		handleAssignViewBlock,
		handleCreateInlineRichTextBlock,
		handleInlineRichTextBlur,
		handleRemoveBlockFromSlot,
		handleSaveRichTextBlock,
		handleSetNodeBlockPresentation,
		handleStartInlineRichTextEditing,
	};
};

export default useLayoutEditorBlockMutations;